import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { authSnapshot, clearAuth } from '@/store/auth';

const API_BASE = `${import.meta.env.VITE_API_URL ?? ''}/admin/v1`;

// Single axios instance for the entire admin SPA. Do NOT create another.
export const httpClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = authSnapshot();
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

// On 401 — clear session and route to login. Do not attempt silent
// refresh from inside the interceptor: refresh flow lives in
// queries/auth.ts so failures route through React Query / UI.
httpClient.interceptors.response.use(
  (resp) => resp,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const path = window.location.pathname;
      // Avoid loops on the login page itself.
      if (path !== '/login') {
        clearAuth();
        const next = encodeURIComponent(path + window.location.search);
        window.location.replace(`/login?next=${next}`);
      }
    }
    return Promise.reject(error);
  },
);

// Convenience helpers — wrap axios so we always return parsed JSON
// without exposing AxiosResponse details to call-sites.
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const resp = await httpClient.get<T>(url, config);
  return resp.data;
}

export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const resp = await httpClient.post<T>(url, body, config);
  return resp.data;
}

export async function apiPatch<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const resp = await httpClient.patch<T>(url, body, config);
  return resp.data;
}

export async function apiDelete<T = void>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const resp = await httpClient.delete<T>(url, config);
  return resp.data;
}
